import { Alert, Box, CircularProgress } from '@mui/material';
import { useActions } from 'ActionsContext';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import MessageToast from '../MessageToast';
import StyledSection from '../styles/StyledSection';
import { StyledCard, StyledCardContent, StyledHeading, StyledTypography } from '../styles/styles';
import PlaceholderContactCompletedView from './PlaceholderContactCompletedView';
import PlaceholderContactDetailsView from './PlaceholderContactDetailsView';
import PlaceholderContactEditForm from './PlaceholderContactEditForm';
import PlaceholderContactMatchCard from './PlaceholderContactMatchCard';
import PlaceholderContactReplaceDialog from './PlaceholderContactReplaceDialog';
import { PlaceholderContactAdminViewProps } from './placeholderContactAdminTypes';
import { usePlaceholderContactAdmin } from './usePlaceholderContactAdmin';

const PlaceholderContactAdmin: React.FC<PlaceholderContactAdminViewProps> = ({
  placeholderId,
  tone = 'default',
}) => {
  const { refreshActions } = useActions();
  const navigate = useNavigate();
  const {
    data,
    loading,
    submitting,
    showReplaceDialog,
    isEditing,
    isCompleted,
    editFormFields,
    validationErrors,
    message,
    handleReplaceContact,
    closeReplaceDialog,
    confirmReplaceContact,
    handleEditContact,
    handleCancelEdit,
    handleEditFormChange,
    updatePlaceholderContact,
    promotePlaceholderContact,
    handleReturnToActions,
    handleReturnToHome,
    handleViewContact,
    dismissMessage,
  } = usePlaceholderContactAdmin({
    placeholderId,
    refreshActions,
    navigate,
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <StyledTypography variant="h6" tone={tone}>
          Placeholder contact not found
        </StyledTypography>
      </Box>
    );
  }

  if (isCompleted) {
    return (
      <>
        <PlaceholderContactCompletedView
          tone={tone}
          onViewContact={handleViewContact}
          onReturnToActions={handleReturnToActions}
          onReturnToHome={handleReturnToHome}
        />
        <MessageToast
          type={message?.type || 'info'}
          message={message?.text || ''}
          open={!!message}
          onClose={dismissMessage}
        />
      </>
    );
  }

  const { placeholder, potentialMatches } = data;

  return (
    <>
      <StyledSection tone={tone} noSidePaddingFrom="xs">
        <Box sx={{ margin: '0 auto', padding: 4 }}>
          <StyledHeading sx={{ mb: 2 }}>Placeholder Contact Details</StyledHeading>

          <StyledTypography sx={{ mb: 4 }}>
            Review and edit the placeholder contact information, or replace it with an existing
            contact.
          </StyledTypography>

          <StyledCard tone="tone1">
            <StyledCardContent sx={{ py: 3 }}>
              {isEditing ? (
                <PlaceholderContactEditForm
                  tone={tone}
                  editFormFields={editFormFields}
                  validationErrors={validationErrors}
                  submitting={submitting}
                  onFieldChange={handleEditFormChange}
                  onSave={updatePlaceholderContact}
                  onCancel={handleCancelEdit}
                />
              ) : (
                <PlaceholderContactDetailsView
                  placeholder={placeholder}
                  tone={tone}
                  submitting={submitting}
                  onEdit={handleEditContact}
                  onPromote={promotePlaceholderContact}
                />
              )}
            </StyledCardContent>
          </StyledCard>

          <StyledHeading sx={{ mt: 4, mb: 2 }}>
            Potential Matches ({potentialMatches.length})
          </StyledHeading>

          {potentialMatches.length === 0 ? (
            <Alert severity="info">
              No potential matches found. You can edit the placeholder contact or promote it as is.
            </Alert>
          ) : (
            <Box>
              {potentialMatches.map((contact) => (
                <PlaceholderContactMatchCard
                  key={contact.id}
                  contact={contact}
                  tone={tone}
                  submitting={submitting}
                  onReplace={handleReplaceContact}
                />
              ))}
            </Box>
          )}
        </Box>
      </StyledSection>

      <PlaceholderContactReplaceDialog
        open={showReplaceDialog}
        submitting={submitting}
        onClose={closeReplaceDialog}
        onConfirm={confirmReplaceContact}
      />

      <MessageToast
        type={message?.type || 'info'}
        message={message?.text || ''}
        open={!!message}
        onClose={dismissMessage}
      />
    </>
  );
};

export default PlaceholderContactAdmin;
